import { FC, useState } from 'react'
import _ from 'lodash'
import { useTranslation } from 'react-i18next'
import { endpoints } from 'modules/endpoints'
import { apiClient } from 'modules/utils/httpClient'
import { refetchFunc } from 'modules/domain/common/hooks'
import { ApprovalStatus, Deal, DealStatusTypes, isApprovalStatusAfter } from 'modules/domain/deal/types'
import { AButton } from 'views/components/Analytics'
import { formatPriceLogistics } from 'modules/utils/numbers/formatPriceLogistics'
import { useAProgress } from 'hooks/useAProgress'
import { usePermissions } from 'views/components/PermissionCheck/usePermissions'
import { DASH } from 'modules/constants'
import { Label, Value } from '../../styled'
import { LogisticForm } from './LogisticForm'
import { Card } from 'views/components/Card/Card'
import { InputWrapper } from '../../../styled'
import { isStatusAfter } from '../../../../helpers'

type LogisticBodyProps = {
  deal: Deal
  dealRefetch: refetchFunc
}

export const LogisticBody: FC<LogisticBodyProps> = ({ deal, dealRefetch }) => {
  const { t } = useTranslation('deal')
  const canApproveLogistic = usePermissions(['deals.logistician_approval'])
  const [isEdit, setIsEdit] = useState(false)
  const isLogisticApproved = isApprovalStatusAfter(deal.approval_status, ApprovalStatus.APPROVED_BY_LOGISTICIANS)
  const isContractSign = isStatusAfter(deal.status, DealStatusTypes.AGREED)
  const isDealAfterNegotiations = isStatusAfter(deal.status, DealStatusTypes.CUSTOMER_NEGOTIATIONS)
  const isSignAvailableByStatus = !isContractSign && isDealAfterNegotiations
  const showInputs = isEdit || (!isDealAfterNegotiations && !deal.is_approved_by_logistician)
  const bothUseOverload = deal.seller_overload && deal.customer_overload
  const [signProgress, signLogistic] = useAProgress(
    () =>
      apiClient.post(endpoints.dealChangeApprovalStatus(deal.id), {
        new_approval_status: ApprovalStatus.APPROVED_BY_LOGISTICIANS,
      }),
    {
      eventName: 'signLogistics',
      analyticsPayload: {
        id: deal.id,
      },
      onSuccess: () => dealRefetch(),
    },
  )
  const canEditLogistics = usePermissions(['deals.can_change_logistics_fields'])
  const logisticEditDisabled = isContractSign || !canEditLogistics

  const price_carrier = deal.price_carrier
  let price_logistics = deal.price_logistics
  let price_logistics_with_overload = deal.price_logistics_with_overload

  const label_price_carrier = t('contractSign.price_carrier')
  let label_price_logistics = t('contractSign.price_logistics')
  let label_price_logistics_with_overload = t('contractSign.price_logistics_with_overload')
  if (isContractSign) {
    //price_carrier = deal.deal_signed_data?.price_logistics || 0
    price_logistics = deal.deal_signed_data?.price_logistics || 0
    price_logistics_with_overload = deal.deal_signed_data?.price_logistics_with_overload || 0

    //label_price_carrier = `${label_price_carrier} (${t('contractSign.onContractSign')})`
    label_price_logistics = `${label_price_logistics} (${t('contractSign.onContractSign')})`
    label_price_logistics_with_overload = `${label_price_logistics_with_overload} (${t('contractSign.onContractSign')})`
  }

  if (!showInputs) {
    return (
      <InputWrapper>
        {isLogisticApproved && (
          <div>
            <Label>{`${t('contractSign.status')}:`}</Label>
            &nbsp;
            <Value>{t('contractSign.statusApproved')} </Value>
          </div>
        )}
        <div>
          <Label> {`${label_price_carrier}:`}</Label>
          &nbsp;
          <Value> {price_carrier ? formatPriceLogistics(price_carrier) : DASH}</Value>
        </div>
        <div>
          <Label color={bothUseOverload ? 'secondary' : 'primary'}>{`${label_price_logistics}:`}</Label>
          &nbsp;
          <Value color={bothUseOverload ? 'secondary' : 'primary'}>
            {price_logistics ? formatPriceLogistics(price_logistics) : DASH}
          </Value>
        </div>
        <div>
          <Label color={bothUseOverload ? 'primary' : 'secondary'}>{`${label_price_logistics_with_overload}:`}</Label>
          &nbsp;
          <Value color={bothUseOverload ? 'primary' : 'secondary'}>
            {price_logistics_with_overload ? formatPriceLogistics(price_logistics_with_overload) : DASH}
          </Value>
        </div>

        {!isLogisticApproved && canApproveLogistic && isSignAvailableByStatus && (
          <div>
            <AButton
              id="contactSignLogistic"
              onClick={signLogistic}
              color="primary"
              size="medium"
              progress={signProgress}
            >
              {t('contractSign.sign')}
            </AButton>
          </div>
        )}
        {!logisticEditDisabled && <Card.EditButton id="editLogistic" onClick={() => setIsEdit(true)} />}
      </InputWrapper>
    )
  }
  return <LogisticForm deal={deal} dealRefetch={dealRefetch} onClose={() => setIsEdit(false)} />
}
